import { useState } from "react";
import { useMainContentRef } from "@/contexts/MainContentRefContext";
import { COMPUTED_TRANSPARENT } from "@/constants/dom";
import { collectBlocks, planPages } from "@/utils/pdfPagination";

const FILENAME = "campaign-overview.pdf";
const LETTER_W_IN = 8.5;
const LETTER_H_IN = 11;
const MARGIN_IN = 0.25;
// Canvas captured at 1.5× device pixels for crisp rendering; divide by this
// when converting canvas pixels back to logical CSS pixels.
const CANVAS_SCALE = 1.5;
// Screen reference pixel definition: 96 CSS px = 1 in.
const CSS_PX_PER_IN = 96;
// JPEG at this quality is 10-15× smaller than PNG with no perceptible loss
// on a dashboard screenshot.
const JPEG_QUALITY = 0.85;
// Background-colour matching tolerance — absorbs JPEG compression artefacts
// and sub-pixel anti-aliasing at card boundaries.
const BG_TOLERANCE = 20;
// Page-break scan window in DOM pixels — wide enough to clear the tallest
// card's inter-card gap at the nominal break point.
const SCAN_BACK_DOM_PX = 300;
// Derived canvas-pixel scan window from the DOM constant above.
const SCAN_BACK_PX = Math.round(SCAN_BACK_DOM_PX * CANVAS_SCALE);
// Sample every Nth pixel when checking for a background row — balances
// accuracy against the cost of iterating the full image data array.
const PIXEL_SAMPLE_STRIDE = 8;

type OverflowSnapshot = {
  el: HTMLElement;
  height: string;
  maxHeight: string;
  overflow: string;
};

// Resolves a CSS custom property token to its fully computed RGB value.
// getPropertyValue returns the raw CSS text; a hidden probe element triggers
// full computation including var() chain resolution.
const resolveToken = (token: string): string => {
  const probe = document.createElement("div");
  probe.style.setProperty("color", `var(${token})`);
  probe.style.setProperty("display", "none");
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  document.body.removeChild(probe);
  return resolved;
};

const useExportPdf = () => {
  const mainRef = useMainContentRef();
  const [isExporting, setIsExporting] = useState(false);

  const exportPdf = async () => {
    const el = mainRef.current;
    if (!el || isExporting) return;

    setIsExporting(true);

    // Mark elements that use background-clip:text so the onclone callback can
    // target them in the cloned document (class names are CSS-Module-hashed).
    const gradientTextEls: HTMLElement[] = [];
    el.querySelectorAll<HTMLElement>("*").forEach((child) => {
      const computed = getComputedStyle(child);
      const webkitBgClip = computed
        .getPropertyValue("-webkit-background-clip")
        .trim();
      const bgClip = (webkitBgClip || computed.backgroundClip).trim();
      const textFill = computed.getPropertyValue("-webkit-text-fill-color");
      if (bgClip === "text" && textFill === COMPUTED_TRANSPARENT) {
        gradientTextEls.push(child);
        // eslint-disable-next-line no-param-reassign
        child.dataset.pdfTextFix = "1";
      }
    });

    // Expand overflow:auto containers so html2canvas captures full scroll height.
    // Collect in document order first, then expand deepest-first: a parent's
    // scrollHeight must be measured after its children are already expanded,
    // otherwise the parent is pinned to its pre-expansion height and
    // html2canvas clips newly-exposed child content.
    const overflowSnapshots: OverflowSnapshot[] = [];
    el.querySelectorAll<HTMLElement>("*").forEach((child) => {
      const computed = getComputedStyle(child);
      // data-pdf-clip marks a list that can be thousands of rows long (the
      // Show all creative bars): it is captured as it looks on screen.
      if (
        child.dataset.pdfClip === undefined &&
        (computed.overflow === "auto" || computed.overflowY === "auto")
      ) {
        overflowSnapshots.push({
          el: child,
          height: child.style.height,
          maxHeight: child.style.maxHeight,
          overflow: child.style.overflow,
        });
      }
    });
    [...overflowSnapshots].reverse().forEach((snapshot) => {
      // eslint-disable-next-line no-param-reassign
      snapshot.el.style.overflow = "visible";
      // eslint-disable-next-line no-param-reassign
      snapshot.el.style.maxHeight = "none";
      // eslint-disable-next-line no-param-reassign
      snapshot.el.style.height = `${snapshot.el.scrollHeight}px`;
    });

    try {
      const bloomPink = resolveToken("--color-light-bloom-mid");
      const bgColor = resolveToken("--color-bg-light");

      const [{ default: html2canvas }, { jsPDF: JsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      // el (.main) is flex:1 in a row-flex 100vh shell, so its offsetHeight is
      // always locked to the viewport even after children are expanded.
      // The expansion pass above already set an explicit inline height on each
      // overflow:auto container — the outermost one (first in document order)
      // has the correct full-content height as its offsetHeight. Capture that
      // directly so html2canvas sees the real content height with no overrides.
      const captureEl =
        overflowSnapshots.length > 0 ? overflowSnapshots[0].el : el;

      const canvas = await html2canvas(captureEl, {
        backgroundColor: bgColor,
        logging: false,
        onclone: (clonedDoc) => {
          // html2canvas can't render background-clip:text — it paints the
          // gradient as a filled block instead of clipping it through the text.
          // Inject a scoped override in the cloned document so the numbers
          // render in a solid brand color without touching the live DOM.
          const style = clonedDoc.createElement("style");
          style.textContent = `
            [data-pdf-text-fix] {
              -webkit-text-fill-color: unset !important;
              background-clip: unset !important;
              -webkit-background-clip: unset !important;
              background: none !important;
              color: ${bloomPink} !important;
            }
          `;
          clonedDoc.head.appendChild(style);

          // html2canvas paints gradients by drawing them to a canvas the size
          // of the element and using it as a pattern; a 0-width or 0-height
          // element makes createPattern throw and aborts the whole export.
          // Strip the background from any such element in the clone.
          // The style read comes first and the layout read (the costly one) only
          // happens for the few elements that actually have a gradient.
          clonedDoc.querySelectorAll<HTMLElement>("*").forEach((node) => {
            const hasGradient =
              clonedDoc.defaultView?.getComputedStyle(node).backgroundImage !==
              "none";
            if (!hasGradient) return;
            const { height, width } = node.getBoundingClientRect();
            if (width < 1 || height < 1) {
              node.style.setProperty("background-image", "none", "important");
            }
          });
        },
        scale: CANVAS_SCALE,
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
      });

      // Scale the captured image to fill the print width of a portrait letter
      // page, then paginate across as many pages as the height requires.
      const contentWidthIn = canvas.width / CANVAS_SCALE / CSS_PX_PER_IN;

      const printWIn = LETTER_W_IN - MARGIN_IN * 2;
      const printHIn = LETTER_H_IN - MARGIN_IN * 2;

      // Fit to page width — never compress the whole dashboard onto one page.
      const fitScale = printWIn / contentWidthIn;
      const imgWIn = contentWidthIn * fitScale;

      const bgRgbMatch = bgColor.match(/\d+/g);
      const bgR = bgRgbMatch ? Number(bgRgbMatch[0]) : 245;
      const bgG = bgRgbMatch ? Number(bgRgbMatch[1]) : 244;
      const bgB = bgRgbMatch ? Number(bgRgbMatch[2]) : 247;

      const ctx2d = canvas.getContext("2d");
      const imgData = ctx2d
        ? ctx2d.getImageData(0, 0, canvas.width, canvas.height)
        : null;

      const isBackgroundRow = (row: number): boolean => {
        if (!imgData) return false;
        const rowStart = row * canvas.width * 4;
        for (let col = 0; col < canvas.width; col += PIXEL_SAMPLE_STRIDE) {
          const byteOffset = rowStart + col * 4;
          if (
            Math.abs(imgData.data[byteOffset] - bgR) > BG_TOLERANCE ||
            Math.abs(imgData.data[byteOffset + 1] - bgG) > BG_TOLERANCE ||
            Math.abs(imgData.data[byteOffset + 2] - bgB) > BG_TOLERANCE
          ) {
            return false;
          }
        }
        return true;
      };

      // A block (card row) taller than a page is the only thing that has to be
      // split mid-block; find a background-colored row near the nominal break
      // so the cut falls in a gap, not through text.
      const breakInsideBlock = (nominal: number, earliest: number): number => {
        const nominalPx = Math.round(nominal * CANVAS_SCALE);
        const earliestPx = Math.round(earliest * CANVAS_SCALE);
        for (
          let row = nominalPx;
          row > nominalPx - SCAN_BACK_PX && row > earliestPx;
          row -= 1
        ) {
          if (isBackgroundRow(row)) return row / CANVAS_SCALE;
        }
        return nominal;
      };

      // Pages break between the dashboard's rows (measured from the live DOM,
      // still expanded at this point), not at an arbitrary pixel row.
      const pages = planPages({
        blocks: collectBlocks(captureEl),
        breakInsideBlock,
        pageHeight: (printHIn * CSS_PX_PER_IN) / fitScale,
        totalHeight: canvas.height / CANVAS_SCALE,
      });

      const pdf = new JsPDF({
        format: "letter",
        orientation: "portrait",
        unit: "in",
      });

      // Each page gets its own cropped image. Drawing the whole capture on
      // every page (shifted) would show the neighbouring pages' content in the
      // top and bottom margins — the duplicated charts and tables.
      pages.forEach(({ end, start }, pageIdx) => {
        if (pageIdx > 0) pdf.addPage();
        const startPx = Math.round(start * CANVAS_SCALE);
        const heightPx =
          Math.min(canvas.height, Math.floor(end * CANVAS_SCALE)) - startPx;
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = heightPx;
        slice
          .getContext("2d")
          ?.drawImage(
            canvas,
            0,
            startPx,
            canvas.width,
            heightPx,
            0,
            0,
            canvas.width,
            heightPx
          );
        const sliceHeightIn =
          (heightPx / CANVAS_SCALE / CSS_PX_PER_IN) * fitScale;
        pdf.addImage(
          slice.toDataURL("image/jpeg", JPEG_QUALITY),
          "JPEG",
          MARGIN_IN,
          MARGIN_IN,
          imgWIn,
          sliceHeightIn
        );
      });

      pdf.save(FILENAME);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("PDF export failed:", err);
    } finally {
      gradientTextEls.forEach((child) => {
        // eslint-disable-next-line no-param-reassign
        delete child.dataset.pdfTextFix;
      });
      overflowSnapshots.forEach(
        ({ el: child, height, maxHeight, overflow }) => {
          // eslint-disable-next-line no-param-reassign
          child.style.height = height;
          // eslint-disable-next-line no-param-reassign
          child.style.maxHeight = maxHeight;
          // eslint-disable-next-line no-param-reassign
          child.style.overflow = overflow;
        }
      );
      setIsExporting(false);
    }
  };

  return { exportPdf, isExporting };
};

export default useExportPdf;
